import { useQueries } from "@tanstack/react-query";
import { getAdminPassword } from "@/lib/auth/storage";
import { adminApi } from "@/lib/api/admin";
import type { MonthlyRegister, MonthlyRow, MonthlyTable } from "@/lib/types";
import {
  monthsForDates,
  weekDatesContaining,
} from "@/lib/reports/build-report";

function mergeMonthlyForDates(
  registers: MonthlyRegister[],
  dates: string[]
): MonthlyRegister {
  const dateSet = new Set(dates);
  const tableMap = new Map<string, MonthlyTable>();

  for (const reg of registers) {
    for (const table of reg.tables) {
      let target = tableMap.get(table.key);
      if (!target) {
        target = { key: table.key, label: table.label, rows: [] };
        tableMap.set(table.key, target);
      }

      for (const row of table.rows) {
        const existing = target.rows.find(
          (r) =>
            r.person_id === row.person_id &&
            r.category === row.category &&
            (r.shift_label ?? "") === (row.shift_label ?? "") &&
            (r.shift_index ?? -1) === (row.shift_index ?? -1)
        );
        if (existing) {
          for (const [d, cell] of Object.entries(row.cells)) {
            if (dateSet.has(d)) existing.cells[d] = cell;
          }
        } else {
          const filteredCells: MonthlyRow["cells"] = {};
          for (const [d, cell] of Object.entries(row.cells)) {
            if (dateSet.has(d)) filteredCells[d] = cell;
          }
          target.rows.push({
            ...row,
            cells: filteredCells,
          });
        }
      }
    }
  }

  const month =
    dates[0]?.slice(0, 7) ?? registers[0]?.month ?? "";

  return {
    month,
    dates: [...dates].sort(),
    tables: [...tableMap.values()],
  };
}

export function useAdminWeekly(anchorDate: string) {
  const adminPassword = getAdminPassword();
  const dates = weekDatesContaining(anchorDate);
  const months = monthsForDates(dates);

  const queries = useQueries({
    queries: months.map((month) => ({
      queryKey: ["admin", "monthly", month],
      queryFn: () => adminApi.getMonthlyRegister(adminPassword!, month),
      enabled: !!adminPassword && months.length > 0,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);
  const error = queries.find((q) => q.error)?.error ?? null;
  const allReady = queries.every((q) => q.data);

  const data =
    allReady && queries.length > 0
      ? mergeMonthlyForDates(
          queries.map((q) => q.data!),
          dates
        )
      : undefined;

  return {
    data,
    dates,
    isLoading,
    isFetching,
    error,
    refetch: () => Promise.all(queries.map((q) => q.refetch())),
  };
}
